import { queryOptions } from '@tanstack/react-query';
import { rpc } from '@/lib/api';
import { ApiError } from '@/lib/errors';
import { readJson, removeKey, writeJson } from '@/lib/storage';
import { publicTenantSchema, type PublicTenant } from './schemas';

declare global {
  interface Window {
    /** Started by the studio's HTML shell so the request runs in parallel with the JS download. */
    __tenantPreload?: { slug: string; data: Promise<unknown> };
  }
}

const CACHE_MAX_AGE_MS = 7 * 24 * 3600_000;
const cacheKey = (slug: string) => `tenant:${slug}`;

/** Last known studio data on this device: repeat visits render at once and refresh in the background. */
function cachedTenant(slug: string): { data: PublicTenant; at: number } | undefined {
  const hit = readJson<{ at: number; data: unknown } | null>(cacheKey(slug), null);
  if (!hit || Date.now() - hit.at > CACHE_MAX_AGE_MS) return undefined;
  const parsed = publicTenantSchema.safeParse(hit.data);
  if (!parsed.success) return undefined;
  // `today` is the studio's wall date; a cached value may be from another day.
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: parsed.data.timezone }).format(new Date());
  return { data: { ...parsed.data, today }, at: hit.at };
}

async function fetchTenant(slug: string): Promise<PublicTenant> {
  const pre = window.__tenantPreload;
  window.__tenantPreload = undefined;
  let raw: unknown;
  if (pre?.slug === slug) raw = await pre.data.catch(() => undefined);
  try {
    raw ??= await rpc('get_tenant_public', { p_slug: slug });
  } catch (e) {
    if (e instanceof ApiError && e.code === 'tenant_not_found') removeKey(cacheKey(slug));
    throw e;
  }
  const tenant = publicTenantSchema.parse(raw);
  writeJson(cacheKey(slug), { at: Date.now(), data: tenant });
  return tenant;
}

export function tenantQuery(slug: string) {
  const cached = cachedTenant(slug);
  return queryOptions({
    queryKey: ['tenant', slug],
    queryFn: () => fetchTenant(slug),
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.at,
    staleTime: 60_000,
    retry: (count, err) => !(err instanceof ApiError && err.code === 'tenant_not_found') && count < 2,
  });
}
